import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import AmazonProductListingEditableField from "./AmazonProductListingEditableField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import EditIcon from '../../../../../designUtils/OpsDashboard/AccountDetails/EditIcon.svg';

function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function normalizeImages(val) {
  if (!val) return [];

  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object") {
          return (
            item.media_location ||
            item.link ||
            item.url ||
            null
          );
        }
        return null;
      })
      .filter(Boolean);
  }

  return [];
}

// ============================================================
// Check if value is nested (object or array of objects)
// ============================================================
function isNestedValue(val) {
  if (val === null || val === undefined || val === "") return false;
  if (typeof val === "object" && !Array.isArray(val)) return true;
  if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object" && val[0] !== null) return true;
  return false;
}

// ============================================================
// Get a short 1-line summary for nested values
// Same logic as getSummary in AmazonProductListingEditableField
// ============================================================
function getSummary(val) {
  if (!val || typeof val !== "object") return val || "—";
  const obj = Array.isArray(val) ? val[0] : val;
  if (!obj || typeof obj !== "object") return String(val);
  return (
    Object.entries(obj)
      .filter(([k]) => !["marketplace_id", "language_tag"].includes(k))
      .map(([, v]) => {
        if (v === null || v === undefined || v === "") return null;
        if (typeof v === "object") return null;
        return String(v);
      })
      .filter(Boolean)
      .slice(0, 4)
      .join(" ") || "—"
  );
}

// ============================================================
// Full nested value renderer (for modal)
// ============================================================
function renderModalColValue(val) {
  if (val === null || val === undefined) return <span style={{ color: "#9ca3af" }}>—</span>;

  if (typeof val === "string") {
    return <span>{val.replace(/<\/?p>/gi, " ").trim()}</span>;
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return <span style={{ color: "#9ca3af" }}>—</span>;
    return val.map((item, i) => (
      <div key={i} style={{ marginBottom: 8 }}>
        {renderModalColValue(item)}
      </div>
    ));
  }

  if (typeof val === "object") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginLeft: 4 }}>
        {Object.entries(val).map(([k, v]) => (
          <div key={k} style={{ fontSize: 11 }}>
            <b style={{ color: "#555", marginRight: 4 }}>{k}:</b>
            {typeof v === "object" && v !== null ? (
              <div style={{ paddingLeft: 8, borderLeft: "1px solid #ccc", margin: "2px 0" }}>
                {renderModalColValue(v)}
              </div>
            ) : (
              <span>{typeof v === "string" ? v.replace(/<\/?p>/gi, " ").trim() : String(v)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(val)}</span>;
}

// ============================================================
// Read More Modal — same style as EditableField nested modal
// ============================================================
function ReadMoreModal({ isOpen, onClose, title, value }) {
  if (!isOpen) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.5)", zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 10,
          width: 560, maxWidth: "95vw", maxHeight: "80vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: "1px solid #e5e7eb", flexShrink: 0
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{title}</span>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#6b7280", lineHeight: 1, padding: "0 4px" }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 18px", overflowY: "auto", flex: 1, fontSize: 12, color: "#374151", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
          {renderModalColValue(value)}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 18px", borderTop: "1px solid #e5e7eb",
          display: "flex", justifyContent: "flex-end", flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              background: "#fff", border: "1px solid #d1d5db",
              color: "#374151", borderRadius: 6,
              padding: "7px 16px", fontSize: 13, cursor: "pointer"
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Catalog / Listing column cell with Read More support
// ============================================================
function NestedReadMoreCell({ val, isBulletField, isImageField, label, columnName }) {
  const [showModal, setShowModal] = useState(false);

  // Image field → show images directly, no Read More needed
  if (isImageField) {
    return (
      <div className="field-image-container">
        {normalizeImages(val).map((img, i) => (
          <img key={i} src={img} alt="img" className="field-image-item" />
        ))}
      </div>
    );
  }

  // Non-nested → render as-is (original behavior)
  if (!isNestedValue(val)) {
    return (
      <div style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
        {renderPlainValue(val, isBulletField)}
      </div>
    );
  }

  // Nested → show summary + inline ...Read More
  const summary = getSummary(val);

  return (
    <>
      <div style={{ fontSize: 12, color: "#374151", wordBreak: "break-word" }}>
        {summary}
        <span
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowModal(true);
          }}
          onMouseEnter={e => e.target.style.textDecoration = "underline"}
          onMouseLeave={e => e.target.style.textDecoration = "none"}
          style={{
            color: "#0073bb", cursor: "pointer", display: "block", marginTop: 4,
            fontSize: 11
          }}
        >
          {" "} ...Read More
        </span>
      </div>

      {/* Modal */}
      <ReadMoreModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={`${label} — ${columnName}`}
        value={val}
      />
    </>
  );
}

// ============================================================
// Plain value renderer (non-nested, original behavior)
// ============================================================
function renderPlainValue(val, isBullet = false) {
  if (val === null || val === undefined) return "—";

  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return "—";

    if (isBullet) {
      return (
        <ul style={{ paddingLeft: '20px', margin: 0 }}>
          {val.map((item, i) => (
            <li key={i} className="field-value-object-group" style={{ marginBottom: '6px' }}>
              {renderPlainValue(item, isBullet)}
            </li>
          ))}
        </ul>
      );
    } else {
      return val.map((item, i) => (
        <div key={i} className="field-value-object-group" style={{ marginBottom: '8px' }}>
          {renderPlainValue(item, isBullet)}
        </div>
      ));
    }
  }

  if (typeof val === "object") {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '4px' }}>
        {Object.entries(val).map(([k, v]) => (
          <div key={k} className="field-value-object" style={{ fontSize: '11px' }}>
            <b style={{ color: '#555', marginRight: '4px' }}>{k}:</b>
            {typeof v === 'object' && v !== null ? (
              <div style={{ paddingLeft: '8px', borderLeft: '1px solid #ccc', margin: '2px 0' }}>
                {renderPlainValue(v, isBullet)}
              </div>
            ) : (
              <span>{typeof v === "string" ? v.replace(/<\/?p>/gi, " ").trim() : String(v)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return String(val);
}

// ============================================================
// Main AmazonProductListingFieldRow
// ============================================================
function AmazonProductListingFieldRow(props) {
  const catalogVal = get(props.field, "catalog.value", null);
  const listingVal = get(props.field, "listing.value", null);

  if (!props.field) return null;

  var isMandatory = get(props.field, "schema.is_mandatory", false);
  var status = get(props.field, "validation.status", "VALID");
  var label = props.field.label || props.field.field || "";
  var description = get(props.field, "schema.description", "");

  var statusClass = status ? status.toLowerCase() : "valid";

  const isImageField =
    props.field.field === "images" ||
    (props.field.field && props.field.field.includes("image_locator")) ||
    (props.field.ui && props.field.ui.input_type === "image");

  const isBulletField =
    (props.field.field && props.field.field.toLowerCase().includes('bullet')) ||
    (label.toLowerCase().includes('bullet'));

  const isUpdated = props.changedFields && props.changedFields.hasOwnProperty(props.field.field);
  const updatedValue = isUpdated ? props.changedFields[props.field.field].value : undefined;

  return (
    <Row className="g-2 px-3 py-2 border-bottom align-items-center m-0 field-row-container">

      {/* Field Label */}
      <Col md={2} className="field-row-label" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
        <p className='mb-0'>
          {label}
          {isMandatory && (
            <span className="field-row-mandatory text-danger ms-1" style={{ fontSize: "17px", fontWeight: "bold" }}>*</span>
          )}
          {description && (
            <span title={description} style={{ cursor: "pointer", marginLeft: "8px", color: "#6c757d" }}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          )}
        </p>
      </Col>

      {/* Catalog Column — with Read More for nested */}
      <Col md={3}>
        <NestedReadMoreCell
          val={catalogVal}
          isBulletField={isBulletField}
          isImageField={isImageField}
          label={label}
          columnName="Catalog"
        />
      </Col>

      {/* Listing Column — with Read More for nested */}
      <Col md={3}>
        <NestedReadMoreCell
          val={listingVal}
          isBulletField={isBulletField}
          isImageField={isImageField}
          label={label}
          columnName="Listing"
        />
      </Col>

      {/* Edit Column */}
      <Col md={4}>
        {isImageField ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap"
            }}
          >
            <div className="field-image-container">
              {normalizeImages(
                isUpdated ? [updatedValue] : listingVal
              ).map((img, i) => (
                <img key={i} src={img} alt="img" className="field-image-item" />
              ))}
            </div>
            <img
              src={EditIcon}
              alt="Edit"
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
              onClick={() => props.action(props.field)}
            />
          </div>
        ) : (
          <AmazonProductListingEditableField
            field={props.field}
            onFieldChange={props.onFieldChange}
            changedValue={updatedValue}
            isUpdated={isUpdated}
            fieldLabel={label}
            readOnly={props.readOnly}
          />
        )}
      </Col>

    </Row>
  );
}

export default AmazonProductListingFieldRow;